/**
 * Home page content — typed, single source for the copy the Home page renders.
 * Copy is taken verbatim from the Claude Design artboard
 * `clients/talent-link-plus/brand/design/Back Office.dc.html` (login view) and
 * its dashboard panel titles; the module mapping is this app's real routes
 * (src/App.tsx `modules` → each moduleConfig.ts).
 */

export const SITE_URL = "https://www.talentlinkplus.com.au";
export const LOGIN_ROUTE = "/login";

export interface LoginCopy {
  kicker: string;
  heading: string;
  lede: string;
  note: string;
  panelTitle: string;
  panelHelp: string;
  panelBody: string;
  signInLabel: string;
  backToSiteLabel: string;
}

export const loginCopy: LoginCopy = {
  kicker: "Back office",
  heading: "Every role, applicant and check in one place.",
  lede: "Staff access to screening forms, ranked shortlists, verification tasks and the audit log.",
  note: "Access is logged. Authorised TalentLink+ staff only.",
  panelTitle: "Staff sign in",
  panelHelp: "Use your @talentlinkplus.com.au account.",
  panelBody: "Sign in on the next screen with your work email and password, a passkey, or an SMS code.",
  signInLabel: "Staff sign in",
  backToSiteLabel: "← Back to site",
};

export interface RuledItem {
  n: string;
  title: string;
  body: string;
}

/** "What staff get" — the four nouns from the login lede, one row each. */
export const staffGets: RuledItem[] = [
  { n: "01", title: "Screening forms", body: "One pre-screening link per role. Candidates answer on their phone; the record opens with the answers attached." },
  { n: "02", title: "Ranked shortlists", body: "Every applicant sits in a pipeline stage — CV received, screening, shortlist, interview, offer, hired." },
  { n: "03", title: "Verification tasks", body: "Onboarding forms, declarations and sign-in readiness live against each employee record." },
  { n: "04", title: "The audit log", body: "Each candidate carries its own history — stage moves, messages sent, forms submitted." },
];

export type StatKey = "openRoles" | "applicantsWeek" | "passedKnockouts" | "checksAwaiting" | "shortlistsReady";

/** Where a KPI value comes from. `none` = no data model in this app yet → rendered as an honest dash. */
export type StatSource = "forms" | "candidates" | "none";

export interface StatSpec {
  key: StatKey;
  /** Artboard label, kept verbatim. */
  label: string;
  source: StatSource;
  /** What the number actually counts (shown under the value). */
  meta: string;
}

export const statSpecs: StatSpec[] = [
  { key: "openRoles", label: "Open roles", source: "forms", meta: "Active screening forms" },
  { key: "applicantsWeek", label: "Applicants this week", source: "candidates", meta: "Candidates added, last 7 days" },
  { key: "passedKnockouts", label: "Passed knockouts", source: "candidates", meta: "Candidates past screening" },
  { key: "checksAwaiting", label: "Checks awaiting result", source: "none", meta: "Check tracking not switched on" },
  { key: "shortlistsReady", label: "Shortlists ready", source: "candidates", meta: "Candidates at shortlist stage" },
];

export interface PanelSpec {
  id: "openRoles" | "verification" | "shortlist" | "audit";
  /** moduleConfig.id that owns the job — the card is hidden when the viewer cannot view that module. */
  moduleId: "prospecting" | "employees";
  /** Artboard panel title, kept verbatim. */
  title: string;
  description: string;
  /** Real route from the owning moduleConfig.ts. */
  to: string;
  ctaLabel: string;
  ctaTitle: string;
}

export const panelSpecs: Record<PanelSpec["id"], PanelSpec> = {
  openRoles: {
    id: "openRoles",
    moduleId: "prospecting",
    title: "Open roles",
    description: "Each role has its own screening form and link. Active forms are the roles currently taking applicants.",
    to: "/prospecting/forms",
    ctaLabel: "Open forms →",
    ctaTitle: "Open the screening forms admin in Prospecting (view only from here)",
  },
  verification: {
    id: "verification",
    moduleId: "employees",
    title: "Verification queue",
    description: "Onboarding forms, tax and super declarations and passkey readiness are tracked per employee.",
    to: "/employees",
    ctaLabel: "Open employees →",
    ctaTitle: "Open the employee roster in Employees (view only from here)",
  },
  shortlist: {
    id: "shortlist",
    moduleId: "prospecting",
    title: "Shortlist",
    description: "Candidates currently at the shortlist stage of the pipeline, most recently updated first.",
    to: "/prospecting",
    ctaLabel: "Open candidates →",
    ctaTitle: "Open the candidate pipeline in Prospecting (view only from here)",
  },
  audit: {
    id: "audit",
    moduleId: "prospecting",
    title: "Audit log",
    description: "Latest candidate record updates. This app has no cross-module audit log yet — each candidate carries its own history.",
    to: "/prospecting",
    ctaLabel: "Open candidates →",
    ctaTitle: "Open the candidate pipeline in Prospecting (view only from here)",
  },
};

export interface ModuleLink {
  moduleId: string;
  label: string;
  body: string;
  to: string;
  title: string;
}

/** The artboard's "HR services" nav, mapped to this tenant's real modules. */
export const workspaceLinks: ModuleLink[] = [
  { moduleId: "clients", label: "Clients", body: "Employer contacts, permissions and notification rules.", to: "/clients", title: "Open Client Management (view only from here)" },
  { moduleId: "companies", label: "Companies", body: "Employer entities that roles are recruited for.", to: "/companies", title: "Open Companies (view only from here)" },
  { moduleId: "employees", label: "Onboarding", body: "Invites, onboarding forms and passkey readiness.", to: "/employees/invites", title: "Open employee invites and onboarding in Employees (view only from here)" },
  { moduleId: "shifts", label: "Shifts", body: "Rosters, check-in and timesheets for placed staff.", to: "/shifts", title: "Open Shifts (view only from here)" },
  { moduleId: "emails", label: "Emails", body: "Sent and received mail against every record.", to: "/emails", title: "Open Emails (view only from here)" },
  { moduleId: "calendars", label: "Calendars", body: "Interviews, bookings and reminders.", to: "/calendars", title: "Open Calendars (view only from here)" },
];

/** Rows shown per panel before the "showing N of M" line — silent truncation is stated in the UI. */
export const PANEL_ROW_LIMIT = 5;
